import { useState, useEffect } from 'react';
import { Euro, Search, Building2, Check, X, User, Calendar, ArrowRight, Percent, Filter } from 'lucide-react';
import { Collab, Influencer, collabsRef, influencersRef, getDocuments, updateDocument } from '../lib/firebase';
import { Dialog } from '@headlessui/react';

interface InfluencerPayment {
  fullName: string;
  campaignName: string;
  brand: string;
  rate: string;
  date: Date | { seconds: number };
  paid: boolean;
  campaignId: string;
  paidPercentage?: number;
}

type DateFilter = 'all' | 'week' | 'month' | '3months' | '6months' | 'year' | 'custom';

export default function Accounting() {
  const [collabs, setCollabs] = useState<Collab[]>([]);
  const [search, setSearch] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [payments, setPayments] = useState<InfluencerPayment[]>([]);
  const [editingPayment, setEditingPayment] = useState<string | null>(null);
  const [tempPercentage, setTempPercentage] = useState<number>(0);
  const [dateFilter, setDateFilter] = useState<DateFilter>('all');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setIsLoading(true);
      const collabsData = await getDocuments<Collab>(collabsRef);
      setCollabs(collabsData);

      // Extract payments from paid campaigns
      const paymentsList: InfluencerPayment[] = [];
      collabsData
        .filter(collab => collab.payment_received)
        .forEach(collab => {
          collab.influencers.forEach(influencer => {
            paymentsList.push({
              fullName: influencer.fullName,
              campaignName: collab.name,
              brand: collab.brand,
              rate: influencer.rate || '0',
              date: collab.date,
              paid: influencer.creator_invoice_sent || false,
              campaignId: collab.id!,
              paidPercentage: influencer.paidPercentage || 0
            });
          });
        });

      setPayments(paymentsList);
      setIsLoading(false);
    } catch (error) {
      console.error('Error loading data:', error);
      setIsLoading(false);
    }
  };

  const handleUpdatePercentage = async (payment: InfluencerPayment, newPercentage: number, updatePaidStatus = false) => {
    try {
      const collab = collabs.find(c => c.id === payment.campaignId);
      if (!collab) return;

      // Ensure percentage is between 0 and 100
      newPercentage = Math.min(100, Math.max(0, newPercentage));

      const updatedInfluencers = collab.influencers.map(inf => {
        if (inf.fullName === payment.fullName) {
          return {
            ...inf,
            creator_invoice_sent: updatePaidStatus ? newPercentage === 100 : inf.creator_invoice_sent,
            paidPercentage: newPercentage
          };
        }
        return inf;
      });

      await updateDocument(collabsRef, payment.campaignId, {
        influencers: updatedInfluencers
      });

      await loadData();
      setEditingPayment(null);
    } catch (error) {
      console.error('Error updating payment percentage:', error);
    }
  };

  const handleTogglePaidStatus = async (payment: InfluencerPayment) => {
    try {
      const collab = collabs.find(c => c.id === payment.campaignId);
      if (!collab) return;

      const updatedInfluencers = collab.influencers.map(inf => {
        if (inf.fullName === payment.fullName) {
          return {
            ...inf,
            creator_invoice_sent: !inf.creator_invoice_sent,
            // Keep the current percentage
            paidPercentage: inf.paidPercentage || 0
          };
        }
        return inf;
      });

      await updateDocument(collabsRef, payment.campaignId, {
        influencers: updatedInfluencers
      });

      await loadData();
    } catch (error) {
      console.error('Error toggling paid status:', error);
    }
  };

  const formatDate = (date: Date | { seconds: number } | undefined) => {
    if (!date) return 'Date inconnue';
    
    // Handle string dates (ISO format)
    if (typeof date === 'string') {
      return new Date(date).toLocaleDateString('fr-FR', {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      });
    }
    
    if ('seconds' in date) {
      return new Date(date.seconds * 1000).toLocaleDateString('fr-FR', {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      });
    }
    
    return new Date(date).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const getDateFromSeconds = (date: { seconds: number }) => {
    return new Date(date.seconds * 1000);
  };

  const isDateInRange = (date: Date | { seconds: number } | string) => {
    let paymentDate: Date;
    
    // Handle string dates (ISO format)
    if (typeof date === 'string') {
      paymentDate = new Date(date);
    } else if ('seconds' in date) {
      paymentDate = getDateFromSeconds(date);
    } else {
      paymentDate = date;
    }
    
    const now = new Date();

    switch (dateFilter) {
      case 'week':
        const weekAgo = new Date();
        weekAgo.setDate(weekAgo.getDate() - 7);
        return paymentDate >= weekAgo && paymentDate <= now;

      case 'month':
        const monthAgo = new Date();
        monthAgo.setMonth(monthAgo.getMonth() - 1);
        return paymentDate >= monthAgo && paymentDate <= now;

      case '3months':
        const threeMonthsAgo = new Date();
        threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);
        return paymentDate >= threeMonthsAgo && paymentDate <= now;

      case '6months':
        const sixMonthsAgo = new Date();
        sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);
        return paymentDate >= sixMonthsAgo && paymentDate <= now;

      case 'year':
        const yearAgo = new Date();
        yearAgo.setFullYear(yearAgo.getFullYear() - 1);
        return paymentDate >= yearAgo && paymentDate <= now;

      case 'custom':
        if (!startDate || !endDate) return true;
        const start = new Date(startDate);
        const end = new Date(endDate);
        end.setHours(23, 59, 59, 999); // Include the entire end date
        return paymentDate >= start && paymentDate <= end;

      default:
        return true;
    }
  };

  const formatCurrency = (amount: string | number) => {
    const num = typeof amount === 'string' ? parseFloat(amount) : amount;
    if (isNaN(num)) return '0 €';
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(num);
  };

  const calculatePaymentAmount = (rate: string, percentage: number) => {
    const totalAmount = parseFloat(rate);
    return (totalAmount * percentage) / 100;
  };

  const filteredPayments = payments.filter(payment => {
    const matchesSearch = 
      payment.fullName.toLowerCase().includes(search.toLowerCase()) ||
      payment.campaignName.toLowerCase().includes(search.toLowerCase()) ||
      payment.brand.toLowerCase().includes(search.toLowerCase());

    const matchesDate = isDateInRange(payment.date);

    return matchesSearch && matchesDate;
  });

  const totalPending = filteredPayments
    .filter(payment => !payment.paid)
    .reduce((sum, payment) => {
      const amountToPay = calculatePaymentAmount(payment.rate, payment.paidPercentage || 0);
      return sum + amountToPay;
    }, 0);

  const totalPaid = filteredPayments
    .filter(payment => payment.paid)
    .reduce((sum, payment) => {
      const paidPercentage = payment.paidPercentage || 0;
      return sum + calculatePaymentAmount(payment.rate, paidPercentage);
    }, 0);

  const getPaymentKey = (payment: InfluencerPayment) => 
    `${payment.campaignId}-${payment.fullName}`;

  const getDateFilterLabel = () => {
    switch (dateFilter) {
      case 'week':
        return '7 derniers jours';
      case 'month':
        return '30 derniers jours';
      case '3months':
        return '3 derniers mois';
      case '6months':
        return '6 derniers mois';
      case 'year':
        return '12 derniers mois';
      case 'custom':
        return 'Période personnalisée';
      default:
        return 'Toutes les dates';
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-accent-purple to-accent-blue flex items-center justify-center">
            <Euro className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-white">Comptabilité influenceur</h1>
            <p className="text-gray-400">Gérez les paiements des influenceurs</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-dark-50 rounded-xl p-6 border border-dark-200">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-white">En attente</h3>
            <span className="text-2xl font-bold text-white">{formatCurrency(totalPending)}</span>
          </div>
          <div className="flex items-center text-gray-400">
            <ArrowRight className="w-4 h-4 mr-1" />
            <span className="text-sm">Paiements à effectuer</span>
          </div>
        </div>

        <div className="bg-dark-50 rounded-xl p-6 border border-dark-200">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-white">Payé</h3>
            <span className="text-2xl font-bold text-white">{formatCurrency(totalPaid)}</span>
          </div>
          <div className="flex items-center text-gray-400">
            <ArrowRight className="w-4 h-4 mr-1" />
            <span className="text-sm">Paiements effectués</span>
          </div>
        </div>
      </div>

      <div className="bg-dark-50 rounded-xl border border-dark-200 p-6">
        <div className="mb-6 flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Rechercher un paiement..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-10"
              />
            </div>
          </div>
          <button
            onClick={() => setIsFilterModalOpen(true)}
            className="flex items-center gap-2 px-4 py-2 bg-dark-200 text-white rounded-lg hover:bg-dark-300 transition-colors"
          >
            <Filter className="w-4 h-4" />
            {getDateFilterLabel()}
          </button>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent-blue"></div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-dark-200">
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-400">Influenceur</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-400">Campagne</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-400">Marque</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-400">Date</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-400">Montant total</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-400">% à facturer</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-400">Montant à facturer</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-400">Statut</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-gray-400">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-dark-200">
                {filteredPayments.map((payment) => {
                  const paymentKey = getPaymentKey(payment);
                  const isEditing = editingPayment === paymentKey;
                  const amountToPay = calculatePaymentAmount(payment.rate, payment.paidPercentage || 0);

                  return (
                    <tr key={paymentKey} className="hover:bg-dark-100/50">
                      <td className="py-3 px-4 text-white">{payment.fullName}</td>
                      <td className="py-3 px-4 text-gray-400">{payment.campaignName}</td>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          <Building2 className="w-4 h-4 text-accent-blue" />
                          <span className="text-gray-400">{payment.brand}</span>
                        </div>
                      </td>
                      <td className="py-3 px-4 text-gray-400">{formatDate(payment.date)}</td>
                      <td className="py-3 px-4 font-medium text-white">{formatCurrency(payment.rate)}</td>
                      <td className="py-3 px-4 text-gray-400">
                        {isEditing ? (
                          <div className="flex items-center gap-2">
                            <input
                              type="number"
                              className="w-20 text-center"
                              value={tempPercentage}
                              onChange={(e) => setTempPercentage(Math.min(100, Math.max(0, parseInt(e.target.value) || 0)))}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  handleUpdatePercentage(payment, tempPercentage);
                                } else if (e.key === 'Escape') {
                                  setEditingPayment(null);
                                }
                              }}
                              autoFocus
                            />
                            <span className="text-gray-400">%</span>
                            <button
                              onClick={() => handleUpdatePercentage(payment, tempPercentage)}
                              className="p-1 text-green-500 hover:text-green-400"
                            >
                              <Check className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => setEditingPayment(null)}
                              className="p-1 text-red-500 hover:text-red-400"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => {
                              setEditingPayment(paymentKey);
                              setTempPercentage(payment.paidPercentage || 0);
                            }}
                            className="hover:text-white transition-colors"
                          >
                            {payment.paidPercentage || 0}%
                          </button>
                        )}
                      </td>
                      <td className="py-3 px-4 font-medium text-accent-blue">
                        {formatCurrency(amountToPay)}
                      </td>
                      <td className="py-3 px-4">
                        {payment.paid ? (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-500/20 text-green-300 border border-green-500/20">
                            Payé
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-500/20 text-yellow-300 border border-yellow-500/20">
                            En attente
                          </span>
                        )}
                      </td>
                      <td className="py-3 px-4 text-right">
                        {!isEditing && (
                          <button
                            onClick={() => handleTogglePaidStatus(payment)}
                            className={payment.paid ? 
                              "text-yellow-500 hover:text-yellow-400" : 
                              "text-accent-blue hover:text-accent-blue/80"
                            }
                            title={payment.paid ? "Marquer comme en attente" : "Marquer comme payé"}
                          >
                            {payment.paid ? <X className="w-5 h-5" /> : <Check className="w-5 h-5" />}
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Date Filter Modal */}
      <Dialog
        open={isFilterModalOpen}
        onClose={() => setIsFilterModalOpen(false)}
        className="fixed inset-0 z-10 overflow-y-auto"
      >
        <div className="flex items-center justify-center min-h-screen p-4">
          <Dialog.Overlay className="fixed inset-0 bg-black bg-opacity-30" />
          <div className="relative bg-dark-50 rounded-xl max-w-md w-full mx-4 p-6 border border-dark-200">
            <div className="mb-6">
              <h3 className="text-lg font-medium text-white">Filtrer par date</h3>
            </div>

            <div className="space-y-4">
              <button
                onClick={() => {
                  setDateFilter('all');
                  setIsFilterModalOpen(false);
                }}
                className={`w-full text-left px-4 py-2 rounded-lg ${
                  dateFilter === 'all' ? 'bg-accent-blue text-white' : 'bg-dark-200 text-gray-400 hover:bg-dark-300'
                }`}
              >
                Toutes les dates
              </button>
              <button
                onClick={() => {
                  setDateFilter('week');
                  setIsFilterModalOpen(false);
                }}
                className={`w-full text-left px-4 py-2 rounded-lg ${
                  dateFilter === 'week' ? 'bg-accent-blue text-white' : 'bg-dark-200 text-gray-400 hover:bg-dark-300'
                }`}
              >
                7 derniers jours
              </button>
              <button
                onClick={() => {
                  setDateFilter('month');
                  setIsFilterModalOpen(false);
                }}
                className={`w-full text-left px-4 py-2 rounded-lg ${
                  dateFilter === 'month' ? 'bg-accent-blue text-white' : 'bg-dark-200 text-gray-400 hover:bg-dark-300'
                }`}
              >
                30 derniers jours
              </button>
              <button
                onClick={() => {
                  setDateFilter('3months');
                  setIsFilterModalOpen(false);
                }}
                className={`w-full text-left px-4 py-2 rounded-lg ${
                  dateFilter === '3months' ? 'bg-accent-blue text-white' : 'bg-dark-200 text-gray-400 hover:bg-dark-300'
                }`}
              >
                3 derniers mois
              </button>
              <button
                onClick={() => {
                  setDateFilter('6months');
                  setIsFilterModalOpen(false);
                }}
                className={`w-full text-left px-4 py-2 rounded-lg ${
                  dateFilter === '6months' ? 'bg-accent-blue text-white' : 'bg-dark-200 text-gray-400 hover:bg-dark-300'
                }`}
              >
                6 derniers mois
              </button>
              <button
                onClick={() => {
                  setDateFilter('year');
                  setIsFilterModalOpen(false);
                }}
                className={`w-full text-left px-4 py-2 rounded-lg ${
                  dateFilter === 'year' ? 'bg-accent-blue text-white' : 'bg-dark-200 text-gray-400 hover:bg-dark-300'
                }`}
              >
                12 derniers mois
              </button>

              <div className="border-t border-dark-200 pt-4">
                <h4 className="text-sm font-medium text-white mb-3">Période personnalisée</h4>
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Date de début</label>
                    <input
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      className="w-full"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Date de fin</label>
                    <input
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      className="w-full"
                    />
                  </div>
                  <button
                    onClick={() => {
                      if (startDate && endDate) {
                        setDateFilter('custom');
                        setIsFilterModalOpen(false);
                      }
                    }}
                    disabled={!startDate || !endDate}
                    className="w-full px-4 py-2 bg-accent-blue text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Appliquer la période
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Dialog>
    </div>
  );
}